import NavButton, { NavButtonRef } from '@atoms/NavButton/NavButton';
import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import Carousel from '@molecules/Carousel/Carousel';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import duplicateArrayItems from '@utils/helpers/duplicateArrayItems';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import useCarouselScrollAnimation from '@utils/hooks/useCarouselScrollAnimation';
import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import SwiperType from 'swiper';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperOptions } from 'swiper/types';

interface Props<T> {
  readonly additionalBreakpoints?: { [width: number]: SwiperOptions };
  readonly carouselClassName?: string;
  readonly carouselHorizontalSpacingClassName?: string;
  readonly dataTestId?: string;
  readonly desktopBreakpoint?: BreakpointEnum.Medium | BreakpointEnum.XLarge;
  readonly disabledNavButtonArrowColor?: string;
  readonly disabledNavButtonBackgroundColor?: string;
  /**
   * enableDomObserver - Enables Swiper's DOM observer to watch for content or layout changes inside the carousel or its parents,
   * allowing it to automatically update without shifting the current slide position.
   */
  readonly enableDomObserver?: boolean;
  readonly epiEdit?: string;
  readonly epiEditTitle?: string;
  readonly headerHorizontalSpacingClassName?: string;
  readonly headerSlot?: JSX.Element;
  readonly headerWrapperClassName?: string;
  readonly headerWrapperDefaultJustifyClassName?: string;
  readonly id: string;
  readonly isLightMode?: boolean;
  readonly isLoadedInView?: boolean;
  readonly isOverflowXl?: boolean;
  readonly isOverflowVisible?: boolean;
  readonly isScrollAnimationEnabled?: boolean;
  readonly isSoftLoadAnimationDisabled?: boolean;
  readonly items: T[];
  readonly loop?: boolean;
  readonly shadowColour?: string;
  readonly shadows?: boolean;
  readonly shouldReinitializeNavigation?: boolean; // for when the carousel is used inside a portal (Modal or Drawer)
  readonly slidesToShowDesktop?: number;
  readonly slidesToShowMobile?: number;
  readonly slidesToShowTablet?: number;
  readonly spaceBetween?: number;
  readonly title?: string;
  readonly titleClassName?: string;
  readonly children: (itemsToDisplay: T[]) => ReactNode;
}

const NavigationCarousel = <T,>({
  dataTestId = 'Navigation-Carousel',
  carouselClassName,
  carouselHorizontalSpacingClassName = 'container',
  disabledNavButtonArrowColor,
  disabledNavButtonBackgroundColor,
  id,
  loop = true,
  title,
  titleClassName = 'h2',
  headerHorizontalSpacingClassName = 'container',
  headerSlot,
  headerWrapperClassName,
  headerWrapperDefaultJustifyClassName = 'justify-end',
  items,
  shadows = false,
  shadowColour,
  slidesToShowDesktop = 3,
  slidesToShowTablet = 2,
  slidesToShowMobile = 1.1,
  desktopBreakpoint = BreakpointEnum.XLarge,
  additionalBreakpoints = {},
  spaceBetween = 20,
  enableDomObserver = false,
  shouldReinitializeNavigation,
  isScrollAnimationEnabled,
  isSoftLoadAnimationDisabled,
  isLoadedInView,
  isLightMode,
  isOverflowXl = true,
  isOverflowVisible = false,
  epiEdit,
  epiEditTitle,
  children,
}: Props<T>) => {
  const [swiperInstance, setSwiperInstance] = useState<Swiper>();
  const [isSwiperInitialized, setIsSwiperInitialized] = useState(false);
  const [isSwiperBeginning, setIsSwiperBeginning] = useState(true);
  const [isSwiperEnd, setIsSwiperEnd] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [currentSlidesPerView, setCurrentSlidesPerView] = useState(0);
  const [shouldLoop, setShouldLoop] = useState(false);
  const screenBreakpoint = useBreakpoint(BreakpointTypeEnum.Screen);
  const viewportBreakpoint = useBreakpoint();
  const isTouchDevice = useIsTouchDevice();
  const leftButtonRef = useRef<NavButtonRef>(null);
  const rightButtonRef = useRef<NavButtonRef>(null);
  const hasMoreItemsToShow =
    currentSlidesPerView > 0 && items.length > currentSlidesPerView;

  const smBreakpoint = BreakpointEnum.Small;
  const mdBreakpoint = BreakpointEnum.Medium;
  const lgBreakpoint = BreakpointEnum.Large;
  const xlBreakpoint = BreakpointEnum.XLarge;

  const isMobile = useMediaQuery(1024);
  const isDesktopScreen =
    (desktopBreakpoint === xlBreakpoint && screenBreakpoint === xlBreakpoint) ||
    (desktopBreakpoint === xlBreakpoint &&
      screenBreakpoint === lgBreakpoint &&
      !isTouchDevice) ||
    (desktopBreakpoint === mdBreakpoint && screenBreakpoint !== smBreakpoint);

  const showNavButtons = hasMoreItemsToShow && isDesktopScreen;
  const shouldDisplayShadowsOnBreakpoint =
    (desktopBreakpoint === xlBreakpoint &&
      viewportBreakpoint !== xlBreakpoint) ||
    (desktopBreakpoint === mdBreakpoint && viewportBreakpoint === smBreakpoint);

  const initialTabletSlidesClassname = {
    2: 'md:[&>.swiper-wrapper>.swiper-slide]:w-1/2',
    3: 'md:[&>.swiper-wrapper>.swiper-slide]:w-1/3',
    4: 'md:[&>.swiper-wrapper>.swiper-slide]:w-1/4',
  }[slidesToShowTablet];

  const initialDesktopSlidesClassname = {
    3: 'xl:[&>.swiper-wrapper>.swiper-slide]:w-1/3',
    4: 'xl:[&>.swiper-wrapper>.swiper-slide]:w-1/4',
    5: 'xl:[&>.swiper-wrapper>.swiper-slide]:w-1/5',
    6: 'xl:[&>.swiper-wrapper>.swiper-slide]:w-1/6',
  }[slidesToShowDesktop];

  const itemsMultiplier = Math.ceil(currentSlidesPerView + 1) * 2;
  const itemsToDisplay = shouldLoop
    ? duplicateArrayItems(items, itemsMultiplier)
    : items;

  const handleSwiperUpdate = (swiper: SwiperType) => {
    const slidesPerView = swiper?.params?.slidesPerView;

    setCurrentSlidesPerView(
      slidesPerView && typeof slidesPerView === 'number' ? slidesPerView : 0
    );
  };

  const handleNavButtonClick = ({
    activeButtonRef,
    inactiveButtonRef,
    newSlideIndex,
  }: {
    activeButtonRef: RefObject<NavButtonRef>;
    inactiveButtonRef: RefObject<NavButtonRef>;
    newSlideIndex: number;
  }) => {
    setTimeout(() => {
      const isActiveButtonDisabled = activeButtonRef.current?.disabled;
      const isInactiveButtonEnabled = !inactiveButtonRef.current?.disabled;

      if (isActiveButtonDisabled && isInactiveButtonEnabled) {
        inactiveButtonRef.current?.focus();
      }
    });

    if (isScrollAnimationEnabled) {
      swiperInstance?.slideTo(newSlideIndex);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Additional Dependencies
  useEffect(() => {
    const shouldLoopCondition = isScrollAnimationEnabled
      ? items.length <= 4 && viewportBreakpoint === smBreakpoint
      : ((desktopBreakpoint === xlBreakpoint && !isTouchDevice) ||
          (desktopBreakpoint === mdBreakpoint &&
            !!viewportBreakpoint &&
            viewportBreakpoint !== smBreakpoint)) &&
        hasMoreItemsToShow &&
        loop;

    setShouldLoop(shouldLoopCondition);
  }, [
    viewportBreakpoint,
    desktopBreakpoint,
    isTouchDevice,
    hasMoreItemsToShow,
    loop,
    items.length,
    isScrollAnimationEnabled,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Additional Dependencies
  useEffect(() => {
    swiperInstance?.slideTo(0, 0);
    if (shouldReinitializeNavigation) swiperInstance?.navigation?.init();
    swiperInstance?.navigation.update();
  }, [swiperInstance, shouldLoop, shouldReinitializeNavigation]);

  useCarouselScrollAnimation({
    swiperInstance,
    shouldAnimateCondition: !!(
      isLoadedInView &&
      isScrollAnimationEnabled &&
      !isMobile
    ),
  });

  return (
    <div className="overflow-swiper w-full" data-testid={dataTestId}>
      <SoftLoadWrapper isAnimationDisabled={isSoftLoadAnimationDisabled}>
        <div
          className={classNames('mb-7.5', {
            'xl:mb-10': desktopBreakpoint === xlBreakpoint,
            'md:mb-10': desktopBreakpoint === mdBreakpoint,
            hidden: !headerSlot && !title && !showNavButtons,
            [`${headerHorizontalSpacingClassName}`]: !isScrollAnimationEnabled,
          })}
        >
          <div
            className={classNames(
              'flex w-full flex-row items-end',
              title || headerSlot
                ? 'justify-between'
                : headerWrapperDefaultJustifyClassName,
              headerWrapperClassName
            )}
          >
            {headerSlot}

            {title && (
              <motion.h2
                {...(isScrollAnimationEnabled
                  ? {
                      initial: { opacity: 0, y: 75 },
                      animate: isLoadedInView ? { opacity: 1, y: 0 } : {},
                      transition: { duration: 0.7, ease: 'easeInOut' },
                    }
                  : {})}
                className={titleClassName}
                data-testid={`${dataTestId}-Title`}
                data-epi-property-name={epiEditTitle}
              >
                {title}
              </motion.h2>
            )}

            <motion.div
              {...(isScrollAnimationEnabled
                ? {
                    initial: { opacity: 0 },
                    animate: isLoadedInView ? { opacity: 1 } : {},
                    transition: { duration: 0.75, delay: 0.5 },
                  }
                : {})}
              className={showNavButtons ? 'flex flex-row gap-5' : 'hidden'}
            >
              <NavButton
                ref={leftButtonRef}
                direction={HorizontalDirectionEnum.Left}
                {...(isScrollAnimationEnabled ? {} : { id })}
                disabled={!shouldLoop && isSwiperBeginning}
                disabledArrowColor={disabledNavButtonArrowColor}
                disabledBackgroundColor={disabledNavButtonBackgroundColor}
                onClick={() =>
                  handleNavButtonClick({
                    activeButtonRef: leftButtonRef,
                    inactiveButtonRef: rightButtonRef,
                    newSlideIndex: slideIndex - 1,
                  })
                }
                isLightMode={isLightMode}
              />

              <NavButton
                ref={rightButtonRef}
                direction={HorizontalDirectionEnum.Right}
                {...(isScrollAnimationEnabled ? {} : { id })}
                disabled={!shouldLoop && isSwiperEnd}
                disabledArrowColor={disabledNavButtonArrowColor}
                disabledBackgroundColor={disabledNavButtonBackgroundColor}
                onClick={() =>
                  handleNavButtonClick({
                    activeButtonRef: rightButtonRef,
                    inactiveButtonRef: leftButtonRef,
                    newSlideIndex: slideIndex + 1,
                  })
                }
                isLightMode={isLightMode}
              />
            </motion.div>
          </div>
        </div>

        <ShadowContainerHorizontal
          showStart={
            shadows &&
            (!isSwiperBeginning || shouldLoop) &&
            shouldDisplayShadowsOnBreakpoint
          }
          showEnd={
            shadows &&
            (!isSwiperEnd || shouldLoop) &&
            shouldDisplayShadowsOnBreakpoint
          }
          shadowColour={shadowColour}
          disableScroll
        >
          <div
            className={classNames(
              'whitespace-normal',
              isScrollAnimationEnabled
                ? 'container'
                : carouselHorizontalSpacingClassName
            )}
            data-epi-property-name={epiEdit}
          >
            <Carousel
              spaceBetween={spaceBetween}
              loop={shouldLoop}
              navId={id}
              className={classNames(
                'w-full',
                {
                  'overflow-visible':
                    isScrollAnimationEnabled || isOverflowVisible,
                  'overflow-xl':
                    desktopBreakpoint === xlBreakpoint &&
                    !isScrollAnimationEnabled &&
                    !isOverflowVisible &&
                    isOverflowXl,
                  'overflow-mobile': desktopBreakpoint === mdBreakpoint,
                  [`${initialTabletSlidesClassname} ${initialDesktopSlidesClassname} md:[&>.swiper-wrapper>.swiper-slide]:pr-5`]:
                    !isSwiperInitialized,
                },
                carouselClassName
              )}
              onResize={handleSwiperUpdate}
              onInit={(swiper) => {
                handleSwiperUpdate(swiper);
                setIsSwiperInitialized(true);
              }}
              observer={enableDomObserver}
              observeParents={enableDomObserver}
              onSwiper={setSwiperInstance}
              watchSlidesProgress
              breakpoints={{
                0: {
                  slidesPerView: slidesToShowMobile,
                },
                768: {
                  slidesPerView: slidesToShowTablet,
                },
                1440: {
                  slidesPerView: slidesToShowDesktop,
                },
                ...additionalBreakpoints,
              }}
              onSlideChange={(swiper) => {
                setSlideIndex(swiper?.realIndex || 0);
              }}
              onSetIsSwiperBeginning={setIsSwiperBeginning}
              onSetIsSwiperEnd={setIsSwiperEnd}
              freeMode={isScrollAnimationEnabled}
              additionalModules={isScrollAnimationEnabled ? [FreeMode] : []}
            >
              {children(itemsToDisplay)}
            </Carousel>
          </div>
        </ShadowContainerHorizontal>
      </SoftLoadWrapper>
    </div>
  );
};

export default NavigationCarousel;
